import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { PageLoading } from "@/shared";
import { isAllowedDocsReturnUrl } from "@/features/help-hub/docs.contract";
import { useAuth } from "@/hooks";
import { useCreateDocsSessionMutation } from "@/redux/services/account/accountApi";

/**
 * First-party handoff for the docs site.
 * A signed-in Attune user is sent back with a docs pass.
 * SuperAdmins also keep the staff query the published docs site already understands.
 */
export function DocsAccess() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const [createDocsSession] = useCreateDocsSessionMutation();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user || !isAllowedDocsReturnUrl(next)) return undefined;

    let cancelled = false;
    createDocsSession()
      .unwrap()
      .then((result) => {
        if (cancelled) return;
        const token = result?.data?.token;
        if (!token) {
          setFailed(true);
          return;
        }
        const url = new URL(next);
        url.hash = `attuneDocs=${encodeURIComponent(token)}`;
        if (user.authority === "superAdmin") {
          url.searchParams.set("docsStaff", "1");
        }
        window.location.assign(url.toString());
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [user, next, createDocsSession]);

  if (user && !isAllowedDocsReturnUrl(next)) {
    return <Navigate to="/" replace />;
  }

  if (failed) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-sm text-gray-700">
        <p>Attune could not open the docs from this sign-in.</p>
        <p className="mt-3">
          <a className="text-blue-700 underline" href="/">
            Back to the dashboard
          </a>
        </p>
      </div>
    );
  }

  return <PageLoading />;
}

export default DocsAccess;
